// FieldHubPage.js — Field Crew Hub dashboard
// Route: #field-hub?project=BASTROP — no login required
// 4-tile mobile-first dashboard: Map / Street Sheet / Utility Hit / Safety
import React from 'react';
import { Map, ClipboardList, AlertTriangle, Shield, Globe } from 'lucide-react';
import { useTranslation } from '../config/translations';
import { useMobile } from '../utils/theme';

const FieldHubPage = ({ darkMode, setCurrentPage }) => {
  const { t, lang, toggleLang } = useTranslation();
  const isMobile = useMobile();

  // Parse project from URL
  const getProject = () => {
    try {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.split('?')[1] || '');
      return params.get('project') || 'BASTROP';
    } catch { return 'BASTROP'; }
  };
  const project = getProject();

  // Determine if today is Friday for weekend banner
  const today = new Date();
  const isFriday = today.getDay() === 5;
  const deadlineText = isFriday ? t('hub_weekend') : t('hub_deadline');

  const bgColor = darkMode ? '#0a1628' : '#f0f4f8';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const cardBorder = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';

  const tiles = [
    {
      key: 'map',
      icon: Map,
      label: t('hub_map'),
      desc: t('hub_map_desc'),
      color: darkMode ? '#3b82f6' : '#0077B6',
      bgTint: darkMode ? '#1e3a5f' : '#e0f2fe',
      onClick: () => {
        window.location.hash = `#shared-map?project=${project}`;
      },
    },
    {
      key: 'streetsheet',
      icon: ClipboardList,
      label: t('hub_streetsheet'),
      desc: t('hub_streetsheet_desc'),
      color: darkMode ? '#22c55e' : '#16a34a',
      bgTint: darkMode ? '#1a3a2a' : '#dcfce7',
      onClick: () => {
        window.location.hash = `#street-sheet?project=${project}&from=hub`;
      },
    },
    {
      key: 'utilityhit',
      icon: AlertTriangle,
      label: t('hub_utilityhit'),
      desc: t('hub_utilityhit_desc'),
      color: darkMode ? '#f97316' : '#dc2626',
      bgTint: darkMode ? '#3a2a1a' : '#fee2e2',
      onClick: () => {
        window.location.hash = `#utility-hit?project=${project}&from=hub`;
      },
    },
    {
      key: 'safety',
      icon: Shield,
      label: t('hub_safety'),
      desc: t('hub_safety_desc'),
      color: darkMode ? '#06b6d4' : '#0d9488',
      bgTint: darkMode ? '#1a2f3a' : '#ccfbf1',
      onClick: () => {
        window.location.hash = `#safety?project=${project}&from=hub`;
      },
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: bgColor,
      padding: isMobile ? '20px 16px' : '40px 24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      {/* Header */}
      <div style={{
        width: '100%',
        maxWidth: '600px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
      }}>
        <div>
          <h1 style={{
            fontSize: isMobile ? '1.4rem' : '1.8rem',
            fontWeight: '700',
            color: textColor,
            margin: 0,
          }}>
            {t('hub_title')}
          </h1>
          <p style={{ color: mutedColor, fontSize: '0.85rem', margin: '4px 0 0' }}>
            {project}
          </p>
        </div>

        {/* Language toggle */}
        <button
          onClick={toggleLang}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${cardBorder}`,
            borderRadius: '20px',
            color: textColor,
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: '600',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Globe size={16} />
          {lang === 'en' ? 'ES' : 'EN'}
        </button>
      </div>

      {/* Tile Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: isMobile ? '12px' : '16px',
        width: '100%',
        maxWidth: '600px',
        marginBottom: '24px',
      }}>
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <button
              key={tile.key}
              onClick={tile.onClick}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: isMobile ? '24px 12px' : '32px 16px',
                minHeight: isMobile ? '130px' : '150px',
                backgroundColor: tile.bgTint,
                border: `1px solid ${cardBorder}`,
                borderRadius: '16px',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                textAlign: 'center',
              }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
              onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: tile.color + '20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Icon size={24} style={{ color: tile.color }} />
              </div>
              <div>
                <div style={{
                  fontSize: isMobile ? '0.95rem' : '1.05rem',
                  fontWeight: '700',
                  color: textColor,
                  marginBottom: '4px',
                }}>
                  {tile.label}
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: mutedColor,
                  lineHeight: '1.3',
                }}>
                  {tile.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Deadline Banner */}
      <div style={{
        width: '100%',
        maxWidth: '600px',
        padding: '14px 20px',
        backgroundColor: darkMode ? '#3a2a1a' : '#fef3c7',
        border: `1px solid ${darkMode ? '#854d0e' : '#fbbf24'}`,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '0.9rem',
        color: darkMode ? '#fbbf24' : '#92400e',
        fontWeight: '600',
      }}>
        <span style={{ fontSize: '1.2rem' }}>&#9200;</span>
        {deadlineText}
      </div>

      {/* LYT branding */}
      <div style={{
        marginTop: '32px',
        fontSize: '0.75rem',
        color: mutedColor,
        textAlign: 'center',
      }}>
        LYT Communications
      </div>
    </div>
  );
};

export default FieldHubPage;
